import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './button';
import { Filter, X } from 'lucide-react';
import { eventService } from '../services';
import { buildYearOptions, getCurrentYear, filterActiveEvents } from '../utils/eventStatus';

const ReservationFilters = ({
  filters = {},
  onFiltersChange,
  onClearFilters,
  onSearch,
  searchPlaceholder = 'Buscar por código de retiro...',
  loading = false
}) => {
  // Estado local para los inputs
  const [localAccessCode, setLocalAccessCode] = useState(filters.accessCode || '');
  const [localStudentName, setLocalStudentName] = useState(filters.studentId || '');
  const [localEventTitle, setLocalEventTitle] = useState(filters.eventId || '');
  const [localStatus, setLocalStatus] = useState(filters.status || 'all');
  const [localYear, setLocalYear] = useState(filters.year || getCurrentYear());
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const yearOptions = buildYearOptions(events);

  // Cargar eventos disponibles
  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const response = await eventService.getEvents();
      setEvents(filterActiveEvents(response));
    } catch (error) {
      console.error('Error al cargar eventos:', error);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  // Cargar eventos al montar el componente
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Sincronizar estado local cuando cambian los filtros externos
  useEffect(() => {
    setLocalAccessCode(filters.accessCode || '');
    setLocalStudentName(filters.studentId || '');
    setLocalEventTitle(filters.eventId || '');
    setLocalStatus(filters.status || 'all');
    setLocalYear(filters.year || getCurrentYear());
  }, [filters.accessCode, filters.studentId, filters.eventId, filters.status, filters.year]);

  const handleClearFilters = () => {
    setLocalAccessCode('');
    setLocalStudentName('');
    setLocalEventTitle('');
    setLocalStatus('all');
    setLocalYear(getCurrentYear());
    onClearFilters();
  };



  const handleAccessCodeChange = (e) => {
    const value = e.target.value;
    setLocalAccessCode(value);
  };

  const handleStudentNameChange = (e) => {
    const value = e.target.value;
    setLocalStudentName(value);
  };



  const handleApplyFilters = () => {
    onFiltersChange({
      ...filters,
      accessCode: localAccessCode,
      studentId: localStudentName,
      eventId: localEventTitle,
      status: localStatus,
      year: localYear,
      applyNow: true
    });
    onSearch();
  };

  const hasActiveFilters =
    localAccessCode !== '' ||
    localStudentName !== '' ||
    localEventTitle !== '' ||
    localStatus !== 'all' ||
    localYear !== getCurrentYear();

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Filter className="w-4 h-4 text-gray-500 mr-2" />
          <h3 className="text-sm font-medium text-gray-700">Filtros de Reservas</h3>
        </div>
        {hasActiveFilters && (
          <Button
            onClick={handleClearFilters}
            variant="outline"
            size="sm"
            className="text-xs"
          >
            <X className="w-3 h-3 mr-1" />
            Limpiar
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Búsqueda por código de acceso */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Código de Retiro</label>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={localAccessCode}
            onChange={handleAccessCodeChange}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        {/* Búsqueda por nombre del Alumno */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Alumno</label>
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={localStudentName}
            onChange={handleStudentNameChange}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          />
        </div>

        {/* Dropdown de funciones */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Función</label>
          <select
            value={localEventTitle}
            onChange={(e) => setLocalEventTitle(e.target.value)}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading || eventsLoading}
          >
            <option value="">Todas las funciones</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.title} - {new Date(event.eventDate).toLocaleDateString()} {event.eventTime}
              </option>
            ))}
          </select>
        </div>

        {/* Filtro por año */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
          <select
            value={localYear}
            onChange={(e) => setLocalYear(e.target.value)}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="all">Todos los años</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Filtro por estado */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
          <select
            value={localStatus}
            onChange={(e) => setLocalStatus(e.target.value)}
            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={loading}
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Reservadas</option>
            <option value="sold">Vendidas</option>
            <option value="completed">Entregadas</option>
            <option value="blocked">Bloqueadas</option>
          </select>
        </div>

        {/* Botón de aplicar filtros */}
        <div className="flex items-end">
          <Button
            onClick={handleApplyFilters}
            className="w-full h-10 bg-blue-600 hover:bg-blue-700"
            disabled={loading}
          >
            Aplicar Filtros
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReservationFilters; 